<template>
  <div class="meeting-panel-content">
    <div class="legend meeting-panel-content__participants">{{ $t("message.PARTICIPANTS") }}</div>
    <Checkbox v-model="allParticipantToggle" class="block" :title="$t('message.ALL')" />
    <Checkbox
      v-for="user in meeting.participants"
      :key="user.id"
      v-model="user.present"
      class="block padded"
      :title="genUserCheckboxTitle(user)"
    />
    <EditableLabelRadio
      v-if="!teamsLoading"
      ref="userSelect"
      :value="participantsSelect"
      :items="userItems"
      label-key="fullName"
      :multiple="true"
      :no-text="true"
      style="height: 0;"
      @input="changeParticipants"
    />
    <button
      class="generic-border generic-border--text-btn generic-border--blue-border mt10em f-w"
      style="margin-bottom:10px;"
      @click="$refs.userSelect.edit()"
    >
      <div class="generic-border__left">
        <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
        {{ $t('message.ADD_PARTICIPANTS') }}
      </div>
    </button>
    <div class="filters-zone__modal__sep" />
    <div class="legend meeting-panel-content__steps">
      {{ $t('message.STEPS') }}
    </div>
    <div v-if="!meeting.steps || meeting.steps.length === 0">
      {{ $tc('message.MEETING_N_STEPS', 0) }}
    </div>
    <div v-for="(step, sIndex) in meeting.steps" :key="`${step.step}-${sIndex}`" style="margin-top:5px">
      {{ decodeHTML(step.name === '' ? $t('message.STEP_NB', { stepNumber: sIndex + 1 }) : step.name) }}
    </div>
    <div class="filters-zone__modal__sep" />
    <div class="legend meeting-panel-content__suggestions">
      {{ $t('message.SUGGESTIONS') }}
    </div>
    <MtSuggestion
      v-for="(suggestion, suIndex) in meeting.suggestions"
      :key="`${suIndex}-${suggestion.id}`"
      :value="suggestion"
      @suggestionChange="saveSuggestion"
    />
    <button
      class="generic-border generic-border--text-btn generic-border--blue-border mt10em f-w"
      style="margin-bottom:10px;"
      @click="addSuggestion()"
    >
      <div class="generic-border__left">
        <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
        {{ $t("message.ADD_A_SUGGESTION") }}
      </div>
    </button>
    <div class="filters-zone__modal__sep" />
    <div class="legend meeting-panel-content__actions">{{ $t('message.ACTIONS') }}</div>
    <div v-if="!meeting.actions || meeting.actions.length === 0">
      {{ $tc('message.N_ACTIONS', 0) }}
    </div>
    <div v-for="action in meeting.actions" :key="action.id">
      {{ decodeHTML(action.text) }}
      <div class="sep" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { GET_TEAMS } from '@/store/mutation-types';
import { START_MEETING } from '@/store/modules/meeting/action-types';
// Import components
import Checkbox from '@/components/checkbox.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import MtSuggestion from '@/components/mt-suggestion.vue';
// Import utils
import { decodeHTML } from '@/utils/main';
import { addExtraBold } from '@/assets/icons';

export default {

  name: 'MeetingPanelContent',

  components: {
    Checkbox,
    EditableLabelRadio,
    Icon,
    MtSuggestion,
  },

  props: {
    meeting: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      addExtraBold,
      START_MEETING,
      participants: [],
      participantsSelect: [],
    };
  },

  computed: {
    currentTeamId() {
      return this.currentTeam ? this.currentTeam.id : null;
    },
    allParticipantToggle: {
      get() {
        return (this.meeting.participants || []).every(p => p.present === true);
      },
      set(newValue) {
        this.meeting.participants.forEach((participant) => {
          participant.present = newValue;
        });
      },
    },
    userItems() {
      return this.teamsData.map(team => ({
        title: team.name + (team.id === this.currentTeamId ? ` (${this.$t('message.OUR_TEAM')})` : ''),
        radioItems: team.users.map(user => Object.assign({ fullName: this.$t('fullNameFormat', user) }, user)),
      }));
    },
    ...mapGetters([
      'companyUsers',
      'companyTeams',
    ]),
    ...mapState({
      teamsLoading: state => state[GET_TEAMS.loadingKey],
      teamsData: state => state[GET_TEAMS.stateKey] || [],
    }),
  },

  watch: {
    // Sync local participants with meeting.participants
    participants() {
      this.$set(this.meeting, 'participants', this.participants);
    },
    meeting() {
      this.$set(
        this.meeting,
        'participants',
        this.genParticipants(this.meeting.participantIds || []),
      );
    },
  },

  mounted() {
    // Participants for this meeting
    this.genParticipants(this.meeting.participantIds || []);
  },

  methods: {
    decodeHTML,
    /**
     * Update participants data objects according to given user IDs
     * @param {number[]} participantIds - User ID of the meeting participants
     */
    genParticipants(participantIds) {
      // Keep 'present' state of current participants
      const presentStates = {};
      this.participants.forEach((user) => {
        presentStates[user.id] = user.present;
      });
      // Create participant list - Copy user object and add 'present' property to it.
      // Needs filtering as some users may be in the participants of the meeting but their accounts
      // may be locked. To reproduce the bug: Add a participant to a meeting and lock his account
      this.participants = participantIds
        .filter(id => !!this.companyUsers[String(id)])
        .map(id => Object.assign(
          cloneDeep(this.companyUsers[String(id)] || {}),
          { present: presentStates[id] !== undefined ? presentStates[id] : true },
        ));
      // Update IDs
      this.participantsSelect = participantIds.slice();
    },
    /**
     * Generate title text for checkbox - Append team name when external team
     * @param {Object} user - User data object
     * @return {string} String to put as title for the checkbox components
     */
    genUserCheckboxTitle(user) {
      const label = this.$t('fullNameFormat', user)
        + (user.team !== this.currentTeamId && this.companyTeams[`${user.team}`]
          ? ` (${this.companyTeams[`${user.team}`].name})`
          : '');
      return decodeHTML(label);
    },
    /**
     * Update participants list according to User ID list in the EditableLabelRadio
     */
    changeParticipants() {
      this.genParticipants(this.participantsSelect);
      this.meeting.participantIds = this.participantsSelect.slice();
    },
    addSuggestion() {
      this.saveSuggestion({
        id: 0,
        text: '',
        created: this.$moment().format('YYYY-MM-DD HH:mm:ss'),
        planning: this.meeting.id,
      });
    },
    saveSuggestion(suggestion) {
      this.$http.post(
        '/server/meetings/saveSuggestion.php',
        {
          id: suggestion.id,
          text: suggestion.text,
          created: suggestion.created,
          planning: suggestion.planning,
        },
      ).then((response) => {
        if (response.data && Array.isArray(response.data)) {
          this.meeting.suggestions = response.data;
        }
      });
    },
  },
};
</script>

<style lang="scss">
.meeting-panel-content {
  &__participants, &__steps, &__suggestions, &__actions {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
  }
}
</style>
